/**
 * Application Service -- API client for mortgage application CRUD, draft management,
 * step progression, bank eligibility, and submission readiness.
 *
 * All methods call the backend via {@link apiClient} (configured base URL + auth headers).
 * Request/response shapes mirror the backend DTOs in `net.hipotech.dto.*`.
 *
 * Error handling: methods throw on HTTP errors (apiClient interceptor handles 401 refresh).
 * Callers should catch and display user-facing errors from the `message` field in the
 * error response body.
 */
import apiClient from './apiClient';

// Minimal data required to create initial draft
export interface MinimalApplicationRequest {
    customerType: string;
    productType: string;
    firstName?: string;
    lastName?: string;
    email?: string;
}

/**
 * Per-bank readiness entry returned by GET /applications/{id}/submission-readiness.
 * Mirrors backend dto.response.BankReadinessInfo.
 */
export interface BankReadinessInfo {
    bankId: string;
    bankName: string;
    bankCode: string;
    fitScore: number;
    satisfied: boolean;
    conditionallySatisfied: boolean;
    totalRequiredDocs: number;
    uploadedDocs: number;
    deferredDocs: number;
    missingDocumentTypes: string[];
    /** All doc types required by this bank (universal + bank-specific), excluding deferrable. */
    requiredDocumentTypes: string[];
    /** Subset of requiredDocumentTypes that have actually been uploaded. */
    uploadedDocumentTypes: string[];
    /** Subset of requiredDocumentTypes that have been formally deferred. */
    deferredDocumentTypes: string[];
}

/**
 * Authoritative submission-readiness payload — the single source of truth the
 * Step 7 review UI and the backend distribution flow both consume.
 */
export interface SubmissionReadiness {
    applicationId: string;
    eligibleBanks: BankReadinessInfo[];
    readyBanks: BankReadinessInfo[];
    incompleteBanks: BankReadinessInfo[];
    eligibleCount: number;
    readyCount: number;
    incompleteCount: number;
}

export interface CreateApplicationRequest {
    // ===== REQUIRED (backend @NotNull — omitting these causes 400) =====
    customerType: string;
    productType: string;
    /** ISO 4217 currency code (e.g. USD, EUR, DOP). REQUIRED by backend. */
    incomeCurrency: string;

    // ===== OPTIONAL — can be provided at creation or via draft updates =====

    // Personal info
    firstName?: string;
    lastName?: string;
    email?: string;
    phoneNumber?: string;
    dateOfBirth?: string;
    nationality?: string;
    passportNumber?: string;
    cedula?: string;

    // Address
    currentAddress?: string;
    city?: string;
    stateProvince?: string;
    country?: string;
    postalCode?: string;

    // Employment
    employmentStatus?: string;
    employerName?: string;
    jobTitle?: string;
    yearsEmployed?: number;
    /** ISO date (yyyy-MM-dd) — backend deserializes to LocalDate. */
    employmentStartDate?: string;

    // Financial
    annualIncome?: number;
    monthlyIncome?: number;
    otherIncome?: number;
    totalAssets?: number;
    totalLiabilities?: number;
    monthlyDebtPayments?: number;
    monthlyDebts?: number;
    monthlyExpenses?: number;
    creditScore?: number;

    // Property
    propertyAddress?: string;
    propertyCity?: string;
    propertyState?: string;
    propertyPostalCode?: string;
    propertyType?: string;
    propertyValue?: number;
    downPayment?: number;
    loanAmountRequested?: number;
    desiredLoanTermMonths?: number;
    loanTerm?: number;
    occupancyType?: string;

    // Preferences & consents
    preferredLanguage?: string;
    marketingConsent?: boolean;
    notes?: string;
    termsAccepted?: boolean;
    dataProcessingConsent?: boolean;
}

export interface Application {
    application_id: string;
    customer_type: string;
    product_type: string;
    current_status: string;
    credit_report_required: boolean;
    credit_assessment_status: string;
    credit_score?: number;
    payment_required: boolean;
    next_step: string;
    personal_info?: Record<string, unknown>;
    financial_info?: Record<string, unknown>;
    property_info?: Record<string, unknown>;
    documents?: Array<{
        document_id: string;
        document_type: string;
        status: string;
        uploaded_at: string;
    }>;
    created_at: string;
    updated_at: string;

    income_currency?: string;
    annual_income_in_dop?: number;
    monthly_income_in_dop?: number;
    property_value_in_dop?: number;
    loan_amount_in_dop?: number;
    exchange_rate_to_dop?: number;
}

export interface ApplicationListResponse {
    success: boolean;
    data: Application[];
    pagination: {
        current_page: number;
        total_pages: number;
        total_items: number;
        items_per_page: number;
        has_next: boolean;
        has_previous: boolean;
    };
}

export interface DocumentReviewItem {
    documentType: string;
    displayName: string;
    displayNameEs: string;
    description: string;
    descriptionEs: string;
    required: boolean;
    uploaded: boolean;
    maxAgeDays: number | null;
    requiredByBanks: string[];
    adjustmentReason: string | null;
    adjustmentReasonEs: string | null;
    source: 'BASELINE' | 'STRATEGY';
}

export interface StrategyAdjustmentItem {
    bankName: string;
    documentType: string;
    action: 'ADD' | 'REMOVE' | 'MAKE_REQUIRED' | 'MAKE_OPTIONAL';
    reason: string;
    reasonEs: string;
    description: string;
    descriptionEs: string;
}

export interface PerBankStatus {
    bankId: string;
    bankName: string;
    totalRequired: number;
    uploadedCount: number;
    missingCount: number;
    complete: boolean;
    missingDocuments: string[];
    missingDocumentNames: string[];
}

export interface DocumentReviewResponse {
    applicationId: string;
    allRequirementsMet: boolean;
    canProceedToPayment: boolean;
    message: string;
    messageEs: string;
    eligibleBankCount: number;
    readyBankCount: number;
    documents: DocumentReviewItem[];
    adjustmentsApplied: StrategyAdjustmentItem[];
    perBankStatus: PerBankStatus[];
    coApplicantReadiness?: {
        ready: boolean;
        skippedForProductType: boolean;
        totalCount: number;
        completedCount: number;
        pendingCount: number;
        pendingParticipants: Array<{
            participantId: string;
            name: string;
            role: string;
            status: string;
        }>;
    };
}

const applicationService = {
    /**
     * Create new application
     * POST /applications
     */
    async createApplication(data: CreateApplicationRequest): Promise<{ success: boolean; data: Application }> {
        const response = await apiClient.post('/applications', data);
        return response.data;
    },

    /**
     * Create an empty DRAFT skeleton — returns a real applicationId immediately.
     * POST /applications/init-draft
     */
    async initDraft(): Promise<{ success: boolean; data: { applicationId: string } }> {
        const response = await apiClient.post('/applications/init-draft');
        return response.data;
    },

    /**
     * Get application details
     * GET /applications/{application_id}
     */
    async getApplication(applicationId: string): Promise<{ success: boolean; data: Application }> {
        const response = await apiClient.get(`/applications/${applicationId}`);
        return response.data;
    },

    /**
     * Update application
     * PUT /applications/{application_id}
     */
    async updateApplication(
        applicationId: string,
        data: Partial<CreateApplicationRequest>
    ): Promise<{ success: boolean; data: Application }> {
        const response = await apiClient.put(`/applications/${applicationId}`, data);
        return response.data;
    },

    /**
     * Convert the product type of a DRAFT application.
     * POST /applications/{applicationId}/convert-type
     *
     * The backend applies all side effects (credit assessment status,
     * property/appraiser clearing) atomically. Only permitted in DRAFT.
     */
    async convertApplicationType(
        applicationId: string,
        targetProductType: 'PERSONAL_PREAPPROVAL' | 'PROPERTY_PREAPPROVAL' | 'FULL_APPLICATION'
    ): Promise<{ success: boolean; data: Application }> {
        const response = await apiClient.post(
            `/applications/${applicationId}/convert-type`,
            { targetProductType }
        );
        return response.data;
    },

    /**
     * List all applications for the current user
     * GET /applications
     */
    async listApplications(params?: {
        status?: string;
        page?: number;
        limit?: number;
    }): Promise<any> {
        const response = await apiClient.get('/applications', { params });
        return response.data;
    },

    /**
     * Check submission requirements
     * GET /applications/{applicationId}/submit/requirements
     */
    async checkSubmissionRequirements(applicationId: string): Promise<{
        success: boolean;
        data: {
            applicationId: string;
            readyForSubmit: boolean;
            requiredFields: string[];
            missingDraftFields: string[];
            message?: string;
        };
    }> {
        const response = await apiClient.get(`/applications/${applicationId}/submit/requirements`);
        return response.data;
    },

    /**
     * GET /admin/applications/{applicationId}/progress
     * Admin-role version of the progress endpoint.
     * The customer endpoint is @RolesAllowed(CUSTOMER) and returns 403 for ADMINs.
     */
    async getApplicationProgressAsAdmin(applicationId: string): Promise<{
        success: boolean;
        data: {
            applicationId: string;
            completedSteps: number;
            totalSteps: number;
            steps: Array<{
                step: number;
                key: string;
                label: string;
                complete: boolean;
                detail: string | null;
            }>;
        };
    }> {
        const response = await apiClient.get(`/admin/applications/${applicationId}/progress`);
        return response.data;
    },

    /**
     * GET /applications/{applicationId}/progress
     * Returns authoritative per-step completion status for a draft application.
     */
    async getApplicationProgress(applicationId: string): Promise<{
        success: boolean;
        data: {
            applicationId: string;
            completedSteps: number;
            totalSteps: number;
            steps: Array<{
                step: number;
                key: string;
                label: string;
                complete: boolean;
                detail: string | null;
            }>;
        };
    }> {
        const response = await apiClient.get(`/applications/${applicationId}/progress`);
        return response.data;
    },

    /**
     * Delete application
     * DELETE /applications/{applicationId}
     * Deletes the application and all attached documents
     */
    async deleteApplication(applicationId: string): Promise<{
        success: boolean;
        message?: string;
    }> {
        const response = await apiClient.delete(`/applications/${applicationId}`);
        return response.data;
    },

    /**
     * Get credit assessment
     * GET /applications/{applicationId}/credit-assessment
     */
    async getCreditAssessment(applicationId: string): Promise<{
        success: boolean;
        data: {
            applicationId: string;
            assessmentStatus: string;
            creditScore: number;
            creditBureau: string;
            reportDate: string;
            assessedAt: string;
        };
    }> {
        const response = await apiClient.get(`/applications/${applicationId}/credit-assessment`);
        return response.data;
    },


    /**
     * Get skipped banks with document completion status (post-payment)
     * GET /applications/{applicationId}/skipped-banks
     */
    async getSkippedBanks(applicationId: string): Promise<{
        success: boolean;
        data: DocumentReviewResponse;
    }> {
        const response = await apiClient.get(`/applications/${applicationId}/skipped-banks`);
        return response.data;
    },

    /**
     * Distribute to additional banks whose requirements are now met (post-payment)
     * POST /applications/{applicationId}/distribute-additional
     */
    async distributeToAdditionalBanks(applicationId: string, bankIds?: string[]): Promise<{
        success: boolean;
        data: {
            applicationId: string;
            distributedCount: number;
            requestedCount: number;
            distributedBankIds: string[];
            message: string;
        };
    }> {
        const response = await apiClient.post(`/applications/${applicationId}/distribute-additional`, {
            bankIds: bankIds || null
        });
        return response.data;
    },

    /**
     * Upload document
     * POST /applications/{application_id}/documents
     *
     * applicantRole must be passed explicitly — the backend never infers it
     * from the document type. Defaults to 'PRIMARY' for backward compatibility
     * with callers that predate Phase 6.
     *
     * Response includes Document Underwriter fields when the feature is enabled:
     *   jobUuid         — non-null when DU will process this document
     *   prepayRequired  — true when an analysis fee is required before processing
     *   prepayFeeAmount — fee amount string, e.g. "50.00"
     *
     * The jobUuid field is the frontend feature flag for DU:
     *   null jobUuid = DU disabled OR document type is NOT_PROCESSED
     */
    async uploadDocument(
        applicationId: string,
        file: File,
        documentType: string,
        applicantRole: 'PRIMARY' | 'CO_APPLICANT' | 'GUARANTOR' | 'SPOUSE' = 'PRIMARY',
    ): Promise<{
        success: boolean;
        data: {
            document_id: string;
            document_type: string;
            filename: string;
            file_size: number;
            status: string;
            uploaded_at: string;
            // Document Underwriter — present when DU is enabled
            jobUuid?: string | null;
            prepayRequired?: boolean;
            prepayFeeAmount?: string;
        };
    }> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('document_type', documentType);
        formData.append('applicant_role', applicantRole);

        const response = await apiClient.post(`/applications/${applicationId}/documents`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            timeout: 120_000, // 2 minutes — large PNGs (7-9MB) need time on slow connections
        });
        return response.data;
    },

    /**
     * List documents for an application
     * GET /applications/{application_id}/documents
     */
    async listDocuments(
        applicationId: string
    ): Promise<{
        success: boolean;
        data: Array<{
            document_id: string;
            document_type: string;
            filename: string;
            status: string;
            uploaded_at: string;
        }>;
    }> {
        const response = await apiClient.get(`/applications/${applicationId}/documents`);
        return response.data;
    },

    /**
     * Delete document
     * DELETE /applications/{application_id}/documents/{document_id}
     */
    async deleteDocument(
        applicationId: string,
        documentId: string
    ): Promise<{ success: boolean; data: { message: string } }> {
        const response = await apiClient.delete(`/applications/${applicationId}/documents/${documentId}`);
        return response.data;
    },

    /**
     * Download document
     * GET /applications/{application_id}/documents/{document_id}
     */
    async downloadDocument(applicationId: string, documentId: string): Promise<Blob> {
        const response = await apiClient.get(`/applications/${applicationId}/documents/${documentId}`, {
            responseType: 'blob'
        });
        return response.data;
    },

    // ── Document Deferrals ─────────────────────────────────────────────

    /**
     * Defer a document — user commits to providing it later if they choose a bank that requires it.
     * POST /applications/{application_id}/documents/defer
     */
    async deferDocument(
        applicationId: string,
        documentType: string
    ): Promise<{ success: boolean; data: { documentType: string; deferred: boolean; totalDeferred: number } }> {
        const response = await apiClient.post(`/applications/${applicationId}/documents/defer`, { documentType });
        return response.data;
    },

    /**
     * Cancel a deferral — user will upload or accept bank exclusion.
     * DELETE /applications/{application_id}/documents/defer/{documentType}
     */
    async undeferDocument(
        applicationId: string,
        documentType: string
    ): Promise<{ success: boolean; data: { documentType: string; deferred: boolean; totalDeferred: number } }> {
        const response = await apiClient.delete(`/applications/${applicationId}/documents/defer/${documentType}`);
        return response.data;
    },

    /**
     * List all deferred document types.
     * GET /applications/{application_id}/documents/deferred
     */
    async listDeferredDocuments(
        applicationId: string
    ): Promise<{ success: boolean; data: { deferredDocuments: string[] } }> {
        const response = await apiClient.get(`/applications/${applicationId}/documents/deferred`);
        return response.data;
    },

    /**
     * Get authoritative submission readiness for an application.
     * GET /applications/{application_id}/submission-readiness
     *
     * Returns the split of profile-eligible banks into:
     *   - readyBanks       — will receive the application on submit (all required
     *                         documents uploaded or formally deferred)
     *   - incompleteBanks  — profile-matched but missing required documents
     *   - eligibleBanks    — union of the two above (all profile-matched banks)
     *
     * The Step 7 review UI uses this instead of recomputing compliance client-side,
     * and the backend submission flow uses the same service so the numbers always
     * agree.
     */
    async getSubmissionReadiness(
        applicationId: string
    ): Promise<{ success: boolean; data: SubmissionReadiness }> {
        const response = await apiClient.get(`/applications/${applicationId}/submission-readiness`);
        return response.data;
    },

    /**
     * Submit application
     * POST /applications/{application_id}/submit
     */
    async submitApplication(applicationId: string): Promise<{ success: boolean; data: { message: string; status: string } }> {
        const response = await apiClient.post(`/applications/${applicationId}/submit`);
        return response.data;
    },

    /**
     * Get user's current draft application
     * GET /applications?status=DRAFT&limit=1
     */
    // ── Phase 7 — Scenario submission ──────────────────────────────────────

    /**
     * Submit application to selected banks as a scenario.
     * Creates the scenario and ScenarioBankRelationship rows atomically with payment confirmation.
     * POST /applications/{applicationId}/submit/scenario
     */
    async submitToScenario(applicationId: string, bankIds: string[]): Promise<{
        success: boolean;
        data: { message: string; status: string; scenarioId: string };
    }> {
        const response = await apiClient.post(
            `/applications/${applicationId}/submit/scenario`,
            { bankIds }
        );
        return response.data;
    },

    /**
     * Get per-bank relationship statuses for the active scenario.
     * GET /applications/{applicationId}/scenario/banks
     */
    async getScenarioBankStatuses(applicationId: string): Promise<{
        success: boolean;
        data: {
            scenarioId: string;
            banks: Array<{
                bankId: string;
                bankName: string;
                duParticipation: 'NOT_PARTICIPATING' | 'PARTICIPATING' | 'DEMO';
                status: 'SUBMITTED' | 'REVIEWING' | 'OFFER_RECEIVED' | 'ACCEPTED' | 'DECLINED_BY_BANK';
                submittedAt: string;
                updatedAt: string;
                offerAmount?: number;
                offerRate?: number;
            }>;
        };
    }> {
        const response = await apiClient.get(`/applications/${applicationId}/scenario/banks`);
        return response.data;
    },

    // ── Phase 8 — Offer acceptance, consent, formal submission ──────────────

    async acceptOffer(applicationId: string, scenarioId: string, bankId: string): Promise<{
        success: boolean;
        data: { relationshipId: string; status: string; message: string };
    }> {
        const response = await apiClient.post(
            `/customer/applications/${applicationId}/scenarios/${scenarioId}/banks/${bankId}/accept-offer`
        );
        return response.data;
    },

    async recordConsent(applicationId: string, scenarioId: string, bankId: string, payload: {
        pathChosen: 'STANDARD' | 'ACCELERATION';
        consentText: string;
        consentTextVersion: string;
    }): Promise<{ success: boolean; data: Record<string, unknown> }> {
        const response = await apiClient.post(
            `/customer/applications/${applicationId}/scenarios/${scenarioId}/banks/${bankId}/consent`,
            payload
        );
        return response.data;
    },

    async submitFormal(applicationId: string, scenarioId: string, bankId: string): Promise<{
        success: boolean;
        data: { transmitted: boolean; transmittedAt: string; bankContact: string; message: string };
    }> {
        const response = await apiClient.post(
            `/customer/applications/${applicationId}/scenarios/${scenarioId}/banks/${bankId}/submit-formal`
        );
        return response.data;
    },

    async mortgageClosed(applicationId: string, scenarioId: string, bankId: string, payload: {
        mortgageValue: number;
        currency?: string;
    }): Promise<{ success: boolean; data: Record<string, unknown> }> {
        const response = await apiClient.post(
            `/customer/applications/${applicationId}/scenarios/${scenarioId}/banks/${bankId}/mortgage-closed`,
            payload
        );
        return response.data;
    },

    async getDraftApplication(
): Promise<{
        success: boolean;
        data: Application | null;
    }> {
        try {
            const response = await apiClient.get('/applications', {
                params: {
                    status: 'DRAFT',
                    limit: 1,
                    sort: 'updated_at',
                    order: 'desc'
                }
            });

            // API returns: { success: true, data: { data: [...apps], total: N } }
            // response.data = { success, data: { data: [...], total } }
            const applications = response.data?.data?.data || response.data?.data || [];
            const appList = Array.isArray(applications) ? applications : [];
            return {
                success: true,
                data: appList.length > 0 ? appList[0] : null
            };
        } catch (error) {
            console.error('Failed to get draft application:', error);
            return {
                success: false,
                data: null
            };
        }
    },

    /**
     * Send document checklist email to the applicant.
     * POST /applications/{applicationId}/documents/send-checklist
     *
     * The frontend builds the document list payload so the backend
     * can render a properly translated, structured email without
     * needing to re-derive the schema.
     */
    async sendDocumentChecklist(
        applicationId: string,
        recipientEmail: string,
        docs: Array<{
            label: string;
            description: string;
            requiredByBanks: string[];
            isUniversal: boolean;
            workflowType: string;
            difficultyTier: number;
            isDeferrable: boolean;
            costHint?: string;
        }>,
        banks: Array<{
            bankName: string;
            totalRequired: number;
            totalBanks: number;
            specificDocs: Array<{
                label: string;
                description: string;
                workflowType: string;
                difficultyTier: number;
                isDeferrable: boolean;
                costHint?: string;
            }>;
        }>,
        locale: string
    ): Promise<{ success: boolean }> {
        const response = await apiClient.post(
            `/applications/${applicationId}/documents/send-checklist`,
            { recipientEmail, docs, banks, locale }
        );
        return { success: response.data?.success ?? true };
    },
};

export default applicationService;